import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateStockGroupInput,
  CreateStockItemInput,
  DeleteStockGroupResult,
  RecordMovementInput,
  SlowMovingReport,
  StockGroup,
  StockGroupList,
  StockItem,
  StockList,
  StockMovementList,
  UpdateStockGroupInput,
  WastageReport,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { billingKeys } from '../billing/use-billing.js';
import {
  createStockGroup,
  createStockItem,
  deleteStockGroup,
  getMovements,
  getSlowMovingReport,
  getStockGroups,
  getStockItem,
  getStockList,
  getWastageReport,
  recordMovement,
  updateStockGroup,
  type MovementOutcome,
  type StockGroupListParams,
  type StockListParams,
} from './stock-api.js';

/**
 * The stock screens' data layer.
 *
 * Every mutation invalidates the ITEM and its MOVEMENTS together, because they are two views of
 * one fact and a screen showing a new quantity above an unchanged history is a screen that looks
 * broken. Nothing here is optimistic: a movement can be refused by the server (there is not
 * enough on the shelf), and showing a shop stock it does not have — even for a second — is the
 * one thing this module must never do.
 */

export const stockKeys = {
  all: ['stock'] as const,
  list: (params: StockListParams) => ['stock', 'list', params] as const,
  item: (itemId: string) => ['stock', 'item', itemId] as const,
  movements: (itemId: string) => ['stock', 'movements', itemId] as const,
  groups: (params: StockGroupListParams) => ['stock', 'groups', params] as const,
  wastage: () => ['stock', 'wastage'] as const,
  slowMoving: (sinceDays: number) => ['stock', 'slow-moving', sinceDays] as const,
};

export function useStockList(
  params: StockListParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<StockList> {
  const client = useApiClient();
  return useQuery({
    queryKey: stockKeys.list(params),
    queryFn: () => getStockList(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useStockItem(
  itemId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<StockItem> {
  const client = useApiClient();
  return useQuery({
    queryKey: stockKeys.item(itemId),
    queryFn: () => getStockItem(client, itemId),
    enabled: (options.enabled ?? true) && itemId.length > 0,
  });
}

export function useStockMovements(
  itemId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<StockMovementList> {
  const client = useApiClient();
  return useQuery({
    queryKey: stockKeys.movements(itemId),
    queryFn: () => getMovements(client, itemId),
    enabled: (options.enabled ?? true) && itemId.length > 0,
  });
}

export function useCreateStockItem(): UseMutationResult<StockItem, unknown, CreateStockItemInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStockItemInput) => createStockItem(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stockKeys.all });
      // The allowance moved. Without this the in-module meter would keep reporting the figure
      // from before the thing that was just created, which is the one moment it has to be right.
      void queryClient.invalidateQueries({ queryKey: billingKeys.usage() });
    },
  });
}

export function useRecordMovement(
  itemId: string,
): UseMutationResult<MovementOutcome, unknown, RecordMovementInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordMovementInput) => recordMovement(client, itemId, input),
    onSuccess: (result) => {
      queryClient.setQueryData(stockKeys.item(itemId), result.item);
      void queryClient.invalidateQueries({ queryKey: stockKeys.movements(itemId) });
      void queryClient.invalidateQueries({ queryKey: ['stock', 'list'] });
    },
  });
}

// --- Groups ---------------------------------------------------------------------------------

/**
 * The headings.
 *
 * Every group mutation invalidates the GROUP LIST and the STOCK LIST together, because one
 * change touches both: renaming a heading changes the label on every row under it, and deleting
 * one lets its items go. A screen that refreshed only the groups would leave the shelf showing a
 * heading that no longer exists.
 */
function invalidateGroupsAndStock(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['stock', 'groups'] });
  void queryClient.invalidateQueries({ queryKey: ['stock', 'list'] });
  void queryClient.invalidateQueries({ queryKey: ['stock', 'item'] });
}

export function useStockGroups(
  params: StockGroupListParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<StockGroupList> {
  const client = useApiClient();
  return useQuery({
    queryKey: stockKeys.groups(params),
    queryFn: () => getStockGroups(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useCreateStockGroup(): UseMutationResult<
  StockGroup,
  unknown,
  CreateStockGroupInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStockGroupInput) => createStockGroup(client, input),
    onSuccess: () => invalidateGroupsAndStock(queryClient),
  });
}

export function useUpdateStockGroup(): UseMutationResult<
  StockGroup,
  unknown,
  { groupId: string; input: UpdateStockGroupInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: UpdateStockGroupInput }) =>
      updateStockGroup(client, groupId, input),
    onSuccess: () => invalidateGroupsAndStock(queryClient),
  });
}

export function useDeleteStockGroup(): UseMutationResult<DeleteStockGroupResult, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => deleteStockGroup(client, groupId),
    onSuccess: () => invalidateGroupsAndStock(queryClient),
  });
}

export function useWastageReport(
  options: { enabled?: boolean } = {},
): UseQueryResult<WastageReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: stockKeys.wastage(),
    queryFn: () => getWastageReport(client),
    enabled: options.enabled ?? true,
  });
}

export function useSlowMovingReport(
  sinceDays = 90,
  options: { enabled?: boolean } = {},
): UseQueryResult<SlowMovingReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: stockKeys.slowMoving(sinceDays),
    queryFn: () => getSlowMovingReport(client, sinceDays),
    enabled: options.enabled ?? true,
  });
}
